/**
 * Picture Upload Zone Component
 * Drag-and-drop interface for uploading presenter pictures
 * Feature: User Management (002-user-management) - Phase 5 US4 - T109
 */

import React, { useState, useRef } from 'react';
import { imageValidator } from '../utils/imageValidator';
import { filenameNormalizer } from '../utils/filenameNormalizer';
import './PictureUploadZone.css';
import { API_BASE_URL } from '../services/ApiService';

export default function PictureUploadZone({ onUploadComplete, eventId }) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const [validationMessages, setValidationMessages] = useState({ errors: [], warnings: [] });
  const [processingFolder, setProcessingFolder] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setProcessingFolder(true);

    try {
      // Handle both files and folders via DataTransferItemList API
      const items = e.dataTransfer.items;
      if (items) {
        const files = await getAllFilesFromItems(items);
        handleFileSelection(files);
      } else {
        // Fallback for browsers that don't support items
        const files = Array.from(e.dataTransfer.files);
        handleFileSelection(files);
      }
    } finally {
      setProcessingFolder(false);
    }
  };

  /**
   * Recursively extract all files from dropped items (including folders)
   */
  const getAllFilesFromItems = async (dataTransferItems) => {
    const files = [];
    const entries = [];

    // Convert items to entries
    for (let i = 0; i < dataTransferItems.length; i++) {
      const item = dataTransferItems[i];
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : item.getAsEntry?.();
        if (entry) {
          entries.push(entry);
        }
      }
    }

    // Recursive function to read directory entries
    const readEntry = (entry) => {
      return new Promise((resolve) => {
        if (entry.isFile) {
          entry.file((file) => {
            files.push(file);
            resolve();
          }, (error) => {
            console.error('Error reading file:', error);
            resolve();
          });
        } else if (entry.isDirectory) {
          const dirReader = entry.createReader();
          dirReader.readEntries(async (entries) => {
            for (const entry of entries) {
              await readEntry(entry);
            }
            resolve();
          }, (error) => {
            console.error('Error reading directory:', error);
            resolve();
          });
        } else {
          resolve();
        }
      });
    };

    // Process all entries
    for (const entry of entries) {
      await readEntry(entry);
    }

    return files;
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    handleFileSelection(files);
  };

  const handleFileSelection = async (files) => {
    if (files.length === 0) return;

    // Filter for image files only
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      setValidationMessages({
        errors: ['No valid image files selected. Only JPEG and PNG images are supported.'],
        warnings: []
      });
      return;
    }

    // Validate files
    const validation = await imageValidator.validateBatch(imageFiles);
    
    setValidationMessages({
      errors: validation.errors,
      warnings: validation.warnings
    });

    // Add valid files to selection
    const fileInfo = imageFiles.map(file => ({
      file,
      name: file.name,
      size: file.size,
      normalizedName: filenameNormalizer.normalize(file.name),
      valid: validation.errors.filter(e => e.includes(file.name)).length === 0
    }));

    setSelectedFiles(fileInfo);
    setUploadResults(null); // Clear previous results
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      // Only send files that passed client-side validation
      const validFiles = selectedFiles.filter(({ valid }) => valid);
      if (validFiles.length === 0) {
        setValidationMessages({
          errors: ['No valid files to upload. Please fix validation errors first.'],
          warnings: []
        });
        setUploading(false);
        return;
      }
      validFiles.forEach(({ file }) => {
        formData.append('pictures', file);
      });

      const token = localStorage.getItem('authToken');
      const uploadUrl = eventId 
        ? `${API_BASE_URL}/admin/pictures/upload?eventId=${eventId}`
        : `${API_BASE_URL}/admin/pictures/upload`;
      const response = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      setUploadResults(result);
      setUploadProgress(100);

      // Immediately clear the file list so we only show the success banner
      setSelectedFiles([]);
      setValidationMessages({ errors: [], warnings: [] });

      // Call callback if provided
      if (onUploadComplete) {
        onUploadComplete(result);
      }

      // Auto-dismiss the success banner after 5 seconds
      setTimeout(() => {
        setUploadResults(null);
        setUploadProgress(0);
      }, 5000);

    } catch (error) {
      setValidationMessages({
        errors: [`Upload failed: ${error.message}`],
        warnings: []
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setSelectedFiles([]);
    setUploadResults(null);
    setValidationMessages({ errors: [], warnings: [] });
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBrowse = () => {
    fileInputRef.current?.click();
  };

  const validFiles = selectedFiles.filter(f => f.valid);
  const invalidFiles = selectedFiles.filter(f => !f.valid);

  return (
    <div className="picture-upload-zone">
      {/* Upload Area */}
      <div
        className={`upload-area ${isDragging ? 'drag-active' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleBrowse}
      >
        <div className="upload-icon">📁</div>
        <h3>Upload Presenter Pictures</h3>
        <p>Drag and drop image files or folders here, or click to browse</p>
        
        <div className="upload-requirements">
          <h4>Requirements:</h4>
          <ul>
            <li>Image format: JPEG or PNG only</li>
            <li>Any aspect ratio accepted (displayed as circle)</li>
            <li>Large images auto-compressed after upload</li>
            <li>Filename: Use presenter's name for automatic matching</li>
          </ul>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          className="file-input"
          accept="image/jpeg,image/png"
          multiple
          onChange={handleFileInputChange}
        />
      </div>

      {/* Processing Folder Indicator */}
      {processingFolder && (
        <div className="processing-folder">
          <div className="spinner">⏳</div>
          <p>Scanning folder for images...</p>
        </div>
      )}

      {/* Validation Messages */}
      {(validationMessages.errors.length > 0 || validationMessages.warnings.length > 0) && (
        <div className="validation-messages">
          {validationMessages.errors.length > 0 && (
            <div className="message-section errors">
              <h4>❌ Validation Errors ({validationMessages.errors.length})</h4>
              <ul className="message-list errors">
                {validationMessages.errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
          
          {validationMessages.warnings.length > 0 && (
            <div className="message-section warnings">
              <h4>⚠️ Warnings ({validationMessages.warnings.length})</h4>
              <ul className="message-list warnings">
                {validationMessages.warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="upload-progress">
          <div className="progress-header">
            <h4>Uploading...</h4>
            <span className="progress-stats">{uploadProgress}%</span>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
          </div>
        </div>
      )}

      {/* Upload Results */}
      {uploadResults && !uploading && (
        <div className="upload-result-banner all-success">
          <div className="result-text">
            <span className="result-icon-inline">✅</span>
            {(() => {
              const uploadedCount = uploadResults.results?.filter(r => r.status === 'uploaded').length || 0;
              const rejectedCount = uploadResults.results?.filter(r => r.status === 'rejected').length || 0;
              return (
                <>
                  {uploadedCount > 0 && (
                    <span>{uploadedCount} image{uploadedCount !== 1 ? 's' : ''} uploaded successfully</span>
                  )}
                  {rejectedCount > 0 && (
                    <span className="result-rejected"> • {rejectedCount} rejected: {uploadResults.results.filter(r => r.status === 'rejected').map(r => r.filename).join(', ')}</span>
                  )}
                </>
              );
            })()}
          </div>
          <button className="result-dismiss" onClick={() => { setUploadResults(null); setUploadProgress(0); }} title="Dismiss">✕</button>
        </div>
      )}

      {/* Selection Summary + Action Buttons */}
      {selectedFiles.length > 0 && !uploading && !uploadResults && (
        <>
          <div className="selection-summary">
            {validFiles.length > 0 && (
              <span className="summary-valid">✓ {validFiles.length} valid</span>
            )}
            {invalidFiles.length > 0 && (
              <span className="summary-invalid">✗ {invalidFiles.length} invalid</span>
            )}
            {invalidFiles.length > 0 && (
              <ul className="invalid-file-list">
                {invalidFiles.map((f, idx) => {
                  const reason = validationMessages.errors.find(e => e.includes(f.name))
                    ?.replace(f.name + ': ', '') || 'Failed validation';
                  return (
                    <li key={idx}><span className="inv-filename">{f.name}</span> — {reason}</li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="action-buttons">
            <button className="btn-secondary" onClick={handleClear}>Clear Selection</button>
            <button className="btn-primary" onClick={handleUpload} disabled={validFiles.length === 0}>
              Upload {validFiles.length} Picture{validFiles.length !== 1 ? 's' : ''}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
